import { FORMAT_DATE } from '@/constants';
import dayjs from '@/utils/dayjs';
import type { DateRange, OpenHours } from '@/types';

const DATE_TIME_FORMAT_SHORT_MONTH = 'MMM DD, h:mm A';

export const toDateOrNull = (value: unknown): Date | null => {
  if (!value) {
    return null;
  }
  if (value instanceof Date) {
    return Number.isNaN(value.getTime()) ? null : value;
  }
  if (typeof value === 'string') {
    const parsed = new Date(value);
    return Number.isNaN(parsed.getTime()) ? null : parsed;
  }
  return null;
};

export const printDateAndTime = (dateStr: string | undefined | null, timezone?: string) => {
  if (!dateStr) {
    return '';
  }

  if (timezone) {
    return dayjs.utc(dateStr).tz(timezone).format(DATE_TIME_FORMAT_SHORT_MONTH);
  }

  return dayjs(dateStr).format(DATE_TIME_FORMAT_SHORT_MONTH);
};

const MINUTE_IN_MS = 60 * 1000;
const HOUR_IN_MS = 60 * MINUTE_IN_MS;

/**
 * Formats a date string as a dynamic relative time or full date/time.
 * - Returns "Just Now" if less than 1 minute ago
 * - Returns "X mins ago" if less than 1 hour ago
 * - Returns full date/time format (printDateAndTime) otherwise
 */
export const printDynamicDateAndTime = (dateStr: string | undefined | null, timezone?: string): string => {
  if (!dateStr) {
    return '';
  }

  const date = dayjs(dateStr);
  const now = dayjs();
  const diffMs = now.diff(date);

  if (diffMs < 0) {
    return printDateAndTime(dateStr, timezone);
  }

  if (diffMs < MINUTE_IN_MS) {
    return 'Just Now';
  }

  if (diffMs < HOUR_IN_MS) {
    const mins = Math.floor(diffMs / MINUTE_IN_MS);
    return `${mins} min${mins === 1 ? '' : 's'} ago`;
  }

  return printDateAndTime(dateStr, timezone);
};

/**
 * Formats a date string as a dynamic relative time or full date/time with day of week.
 * - Returns "Just Now" if less than 1 minute ago
 * - Returns "X mins ago" if less than 1 hour ago
 * - Returns full date/time format "Day of Week, Month Day, Time AM/PM" otherwise
 * Example: "Thursday, Sept 18, 8:00 AM"
 */
export const printDynamicDayDateTime = (dateStr: string | undefined | null, timezone?: string): string => {
  if (!dateStr) {
    return '';
  }

  const date = dayjs(dateStr);
  const now = dayjs();
  const diffMs = now.diff(date);

  if (diffMs < 0) {
    if (timezone) {
      return dayjs.utc(dateStr).tz(timezone).format('dddd, MMM D, h:mm A');
    }
    return dayjs(dateStr).format('dddd, MMM D, h:mm A');
  }

  if (diffMs < MINUTE_IN_MS) {
    return 'Just Now';
  }

  if (diffMs < HOUR_IN_MS) {
    const mins = Math.floor(diffMs / MINUTE_IN_MS);
    return `${mins} min${mins === 1 ? '' : 's'} ago`;
  }

  if (timezone) {
    return dayjs.utc(dateStr).tz(timezone).format('dddd, MMM D, h:mm A');
  }

  return dayjs(dateStr).format('dddd, MMM D, h:mm A');
};

export const printDate = (dateStr: string | undefined | null) => {
  if (!dateStr) {
    return '';
  }

  return dayjs(dateStr).format(FORMAT_DATE);
};

export const formatTime = (time: string | Date | null | undefined, timezone?: string): string => {
  if (!time) {
    return '';
  }

  const isTimeOnlyString = typeof time === 'string' && /^\d{2}:\d{2}$/.test(time);
  const timeParsed =
    timezone && !isTimeOnlyString
      ? dayjs.utc(time).tz(timezone)
      : typeof time === 'string'
        ? dayjs(time, 'HH:mm')
        : dayjs(time);
  const minutes = timeParsed.minute();

  if (minutes === 0) {
    return timeParsed.format('hA');
  }

  return timeParsed.format('h:mmA');
};

export const formatTimeRange = (
  startTime: string | Date | null | undefined,
  endTime?: string | Date | null,
  timezone?: string
): string => {
  const startFormatted = formatTime(startTime, timezone);

  if (!endTime) {
    return startFormatted;
  }

  const endFormatted = formatTime(endTime, timezone);
  return `${startFormatted} - ${endFormatted}`;
};

export const getWeekDateRange = (date: Date = new Date()): DateRange => {
  const startOfWeek = dayjs(date).startOf('week').format('YYYY-MM-DD');
  const endOfWeek = dayjs(date).endOf('week').format('YYYY-MM-DD');

  return {
    startDate: startOfWeek,
    endDate: endOfWeek,
  };
};

const DAY_NAMES = ['Sunday', 'Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday'] as const;

type FormattedOpenHours = {
  dayLabel: string;
  timeRange: string;
  comment?: string;
  displayText: string;
};

const normalizeTime = (time: string): string => {
  if (!time) {
    return '';
  }

  const trimmed = time.trim();
  const upper = trimmed.toUpperCase();

  if (upper.includes('AM') || upper.includes('PM')) {
    return trimmed.replace(/\s+/g, '');
  }

  try {
    const parsed = dayjs(trimmed, 'HH:mm');
    if (parsed.isValid()) {
      return parsed.format('h:mmA').replace(/\s+/g, '');
    }
  } catch {
    return trimmed;
  }

  return trimmed;
};

const formatHoursTimeRange = (openAt: string, closeAt: string): string => {
  const open = normalizeTime(openAt);
  const close = normalizeTime(closeAt);
  return `${open}-${close}`;
};

export const formatOpenHours = (openHours: OpenHours[] | undefined): FormattedOpenHours[] => {
  if (!openHours || openHours.length === 0) {
    return [];
  }

  const sortedOpenHours = [...openHours].sort((a, b) => {
    if (a.day !== b.day) {
      return a.day - b.day;
    }

    return a.openAt.localeCompare(b.openAt);
  });

  return sortedOpenHours.map((hour) => {
    const dayLabel = DAY_NAMES[hour.day % 7];
    const timeRange = hour.isClosed ? 'Closed' : formatHoursTimeRange(hour.openAt, hour.closeAt);

    return {
      dayLabel,
      timeRange,
      comment: hour.comment,
      displayText: `${dayLabel} ${timeRange}`,
    };
  });
};
